import { 
  MetaApiConfig, 
  MetaApiMessageResponse, 
  WhatsAppTemplate, 
  WhatsAppTemplateDetailed, 
  WhatsAppPhoneNumber,
  OnPremisesRegistrationRequest,
  OnPremisesRegistrationResponse,
  OnPremisesVerificationRequest
} from '../types/FireAgent';

class MetaApiService {
  private config: MetaApiConfig | null = null;

  setConfig(config: MetaApiConfig) {
    this.config = config;
  }

  /**
   * Formata telefone para formato E.164 (exigido pela API do Meta)
   * Formato: +5511999999999 (código do país + número)
   */
  private formatPhoneToE164(phone: string, defaultCountryCode: string = '55'): string {
    if (!phone) return '';
    
    // Remove tudo exceto números
    let cleaned = phone.replace(/\D/g, '');
    
    // Se já começar com código do país, manter
    if (cleaned.startsWith(defaultCountryCode)) {
      return `+${cleaned}`;
    }
    
    // Se começar com 0, remover (DDD brasileiro)
    if (cleaned.startsWith('0')) {
      cleaned = cleaned.substring(1);
    }
    
    // Se tiver 10 ou 11 dígitos (telefone brasileiro), adicionar código do país
    if (cleaned.length === 10 || cleaned.length === 11) {
      return `+${defaultCountryCode}${cleaned}`;
    }
    
    // Se já tiver código do país mas sem o +
    if (cleaned.length > 11) {
      return `+${cleaned}`;
    }
    
    // Caso padrão: adicionar código do país
    return `+${defaultCountryCode}${cleaned}`;
  }

  private getBaseUrl(): string {
    if (!this.config) {
      throw new Error('Configuração do Meta API não encontrada');
    }
    
    // Se for On-Premises, usar a URL configurada
    if (this.config.apiType === 'on-premises' && this.config.onPremisesBaseUrl) {
      return this.config.onPremisesBaseUrl;
    }
    
    // Padrão: Cloud API (Graph API)
    const version = this.config.version || 'v24.0';
    return `https://graph.facebook.com/${version}`;
  }

  private getHeaders(): HeadersInit {
    if (!this.config) {
      throw new Error('Configuração do Meta API não encontrada');
    }
    return {
      'Authorization': `Bearer ${this.config.accessToken}`,
      'Content-Type': 'application/json'
    };
  }

  async testConnection(): Promise<boolean> {
    try {
      if (!this.config || !this.config.phoneNumberId) {
        throw new Error('Configuração do Meta API não encontrada ou Phone Number ID não configurado');
      }

      // Validar Phone Number ID
      this.validatePhoneNumberId(this.config.phoneNumberId);

      // Testar conexão tentando buscar informações do número de telefone
      const url = `${this.getBaseUrl()}/${this.config.phoneNumberId}`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: this.getHeaders()
      });
      
      // 200 ou 400 (se o endpoint não existir mas o token estiver válido) são aceitáveis
      // O importante é que não seja 401 (não autorizado)
      return response.status === 200 || (response.status === 400 && !response.statusText.includes('Unauthorized'));
    } catch (error: any) {
      console.error('❌ Erro ao testar conexão:', error);
      return false;
    }
  }

  async listWhatsAppTemplates(): Promise<WhatsAppTemplate[]> {
    try {
      if (!this.config || !this.config.accessToken) {
        throw new Error('Configuração do Meta API não encontrada ou Access Token não configurado');
      }

      // Para listar templates, precisamos do Business Account ID ou usar o Phone Number ID
      // Vamos tentar primeiro com Business Account ID, se não tiver, usamos uma abordagem alternativa
      let url: string;
      let wabaId: string | null = null;
      
      // Para listar templates, é OBRIGATÓRIO ter o Business Account ID (WABA ID)
      // O Phone Number ID não é suficiente para listar templates
      if (!this.config.businessAccountId) {
        throw new Error(
          'Business Account ID (WABA ID) é obrigatório para listar templates.\n\n' +
          'Como encontrar o Business Account ID:\n' +
          '1. Acesse o Meta Business Suite (business.facebook.com)\n' +
          '2. Vá em Configurações > Contas do WhatsApp\n' +
          '3. Selecione sua conta do WhatsApp Business\n' +
          '4. O Business Account ID (WABA ID) aparece no topo da página ou na URL\n' +
          '5. Configure este ID nas Configurações > Integração > Meta API\n\n' +
          'Nota: O Phone Number ID é usado para ENVIAR mensagens, mas o Business Account ID é necessário para LISTAR templates.'
        );
      }

      // Usar Business Account ID
      wabaId = this.config.businessAccountId;
      url = `${this.getBaseUrl()}/${wabaId}/message_templates`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: this.getHeaders()
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage = errorData.error?.message || `Erro ${response.status}: ${response.statusText}`;
        const errorCode = errorData.error?.code;
        const errorType = errorData.error?.type;
        
        if (response.status === 404) {
          throw new Error('Endpoint de templates não encontrado. Verifique se o Business Account ID está correto nas configurações.');
        }
        
        if (response.status === 403) {
          throw new Error('Acesso negado. Verifique se o Access Token tem permissão para listar templates (whatsapp_business_messaging ou whatsapp_template_management).');
        }
        
        if (response.status === 401) {
          throw new Error('Token inválido ou expirado. Verifique o Access Token nas configurações.');
        }
        
        throw new Error(`Erro ao buscar templates: ${errorMessage} (Código: ${errorCode || response.status})`);
      }

      const data = await response.json();
      
      // A resposta da Meta API vem no formato:
      // { data: [{ name, status, category, language, components, ... }] }
      const templatesData = data.data || [];
      // Converter para o formato WhatsAppTemplate
      const templates: WhatsAppTemplate[] = templatesData.map((template: any) => {
        const converted = {
          id: template.id,
          name: template.name,
          status: template.status === 'APPROVED' ? 'APPROVED' : template.status === 'PENDING' ? 'PENDING' : 'REJECTED',
          category: template.category === 'MARKETING' ? 'MARKETING' : template.category === 'AUTHENTICATION' ? 'AUTHENTICATION' : 'UTILITY',
          language: template.language || 'pt_BR',
          components: template.components || [],
          body: template.components?.find((c: any) => c.type === 'BODY')?.text || '',
          header: template.components?.find((c: any) => c.type === 'HEADER')?.text || '',
          footer: template.components?.find((c: any) => c.type === 'FOOTER')?.text || ''
        };
        return converted;
      });
      
      // Filtrar apenas templates aprovados
      const approvedTemplates = templates.filter((t: WhatsAppTemplate) => t.status === 'APPROVED');
      
      return approvedTemplates;
    } catch (error: any) {
      console.error('❌ Erro ao listar templates:', error);
      console.error('📋 Detalhes do erro:', {
        message: error.message,
        stack: error.stack,
        config: this.config ? {
          hasAccessToken: !!this.config.accessToken,
          hasPhoneNumberId: !!this.config.phoneNumberId,
          hasBusinessAccountId: !!this.config.businessAccountId
        } : 'Config não disponível'
      });
      // Re-lançar o erro para que a interface possa exibir a mensagem
      throw error;
    }
  }

  async listAllTemplates(): Promise<WhatsAppTemplateDetailed[]> {
    try {
      if (!this.config || !this.config.accessToken) {
        throw new Error('Configuração do Meta API não encontrada ou Access Token não configurado');
      }

      if (!this.config.businessAccountId) {
        throw new Error('Business Account ID (WABA ID) é obrigatório para listar templates.');
      }

      const url = `${this.getBaseUrl()}/${this.config.businessAccountId}/message_templates`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: this.getHeaders()
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage = errorData.error?.message || `Erro ${response.status}: ${response.statusText}`;
        throw new Error(`Erro ao buscar templates: ${errorMessage}`);
      }

      const data = await response.json();
      const templatesData = data.data || [];
      
      // Converter para o formato WhatsAppTemplateDetailed com todos os detalhes
      const templates: WhatsAppTemplateDetailed[] = templatesData.map((template: any) => {
        return {
          id: template.id,
          name: template.name,
          status: template.status === 'APPROVED' ? 'APPROVED' : template.status === 'PENDING' ? 'PENDING' : 'REJECTED',
          category: template.category === 'MARKETING' ? 'MARKETING' : template.category === 'AUTHENTICATION' ? 'AUTHENTICATION' : 'UTILITY',
          language: template.language || 'pt_BR',
          components: template.components || [],
          body: template.components?.find((c: any) => c.type === 'BODY')?.text || '',
          header: template.components?.find((c: any) => c.type === 'HEADER')?.text || '',
          footer: template.components?.find((c: any) => c.type === 'FOOTER')?.text || '',
          created_at: template.created_at,
          updated_at: template.updated_at,
          quality_rating: template.quality_rating,
          rejection_reason: template.rejection_reason
        };
      });
      
      return templates;
    } catch (error: any) {
      console.error('❌ Erro ao listar templates:', error);
      console.error('📋 Detalhes do erro:', {
        message: error.message,
        stack: error.stack,
        config: this.config ? {
          hasAccessToken: !!this.config.accessToken,
          hasPhoneNumberId: !!this.config.phoneNumberId,
          hasBusinessAccountId: !!this.config.businessAccountId
        } : 'Config não disponível'
      });
      // Re-lançar o erro para que a interface possa exibir a mensagem
      throw error;
    }
  }

  async listPhoneNumbers(): Promise<WhatsAppPhoneNumber[]> {
    try {
      if (!this.config || !this.config.accessToken) {
        throw new Error('Configuração do Meta API não encontrada ou Access Token não configurado');
      }

      if (!this.config.businessAccountId) {
        throw new Error('Business Account ID (WABA ID) é obrigatório para listar números de telefone.');
      }

      // Solicitar campos adicionais para verificar o status real do número
      const fields = 'id,display_phone_number,verified_name,code_verification_status,account_status,status,quality_rating,throughput,last_onboarded_time,eligibility_for_api_business_global_search';
      const url = `${this.getBaseUrl()}/${this.config.businessAccountId}/phone_numbers?fields=${fields}`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: this.getHeaders()
      });

      const data = await response.json();

      if (!response.ok) {
        let errorMessage = data.error?.message || data.error?.error_user_msg || `Erro ${response.status}: ${response.statusText}`;
        
        if (response.status === 401) {
          errorMessage = 'Token de acesso inválido ou expirado. Verifique o Access Token nas configurações.';
        } else if (response.status === 403) {
          errorMessage = 'Sem permissão para acessar os números de telefone. Verifique as permissões do Access Token.';
        } else if (response.status === 404) {
          errorMessage = 'Business Account ID não encontrado. Verifique se o ID está correto nas configurações.';
        }
        
        throw new Error(errorMessage);
      }

      return data.data || [];
    } catch (error: any) {
      throw error;
    }
  }

  /**
   * Obtém detalhes de um número de telefone específico, incluindo status de verificação
   */
  async getPhoneNumberDetails(phoneNumberId: string): Promise<WhatsAppPhoneNumber> {
    try {
      if (!this.config || !this.config.accessToken) {
        throw new Error('Configuração do Meta API não encontrada ou Access Token não configurado');
      }

      if (!this.config.businessAccountId) {
        throw new Error('Business Account ID (WABA ID) é obrigatório.');
      }

      // Validar Phone Number ID
      this.validatePhoneNumberId(phoneNumberId);

      const url = `${this.getBaseUrl()}/${phoneNumberId}`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: this.getHeaders()
      });

      const data = await response.json();

      if (!response.ok) {
        let errorMessage = data.error?.message || data.error?.error_user_msg || `Erro ${response.status}: ${response.statusText}`;
        
        if (response.status === 401) {
          errorMessage = 'Token de acesso inválido ou expirado. Verifique o Access Token nas configurações.';
        } else if (response.status === 403) {
          errorMessage = 'Sem permissão para acessar este número de telefone. Verifique as permissões do Access Token.';
        } else if (response.status === 404) {
          errorMessage = `Phone Number ID "${phoneNumberId}" não encontrado. Verifique se o ID está correto e se o número está registrado no Meta Business Manager.`;
        }
        
        throw new Error(errorMessage);
      }

      return data as WhatsAppPhoneNumber;
    } catch (error: any) {
      throw error;
    }
  }

  private validatePhoneNumberId(phoneNumberId: string): void {
    // Phone Number ID deve ser um número (sem +, sem espaços, sem caracteres especiais)
    // Não deve ser um número de telefone formatado
    if (!phoneNumberId || phoneNumberId.trim() === '') {
      throw new Error('Phone Number ID não pode estar vazio');
    }
    
    // Verificar se contém caracteres que indicam formato de telefone
    if (phoneNumberId.includes('+') || phoneNumberId.includes('(') || phoneNumberId.includes(')') || phoneNumberId.includes('-') || phoneNumberId.includes(' ')) {
      throw new Error('Phone Number ID parece ser um número de telefone formatado. O Phone Number ID deve ser apenas números (ex: 123456789012345). Encontre o ID correto no Meta Business Manager → WhatsApp → API Setup.');
    }
    
    // Verificar se é apenas números
    if (!/^\d+$/.test(phoneNumberId)) {
      throw new Error('Phone Number ID deve conter apenas números. Encontre o ID correto no Meta Business Manager → WhatsApp → API Setup.');
    }
  }

  async sendTextMessage(phoneNumber: string, message: string): Promise<MetaApiMessageResponse> {
    try {
      if (!this.config || !this.config.phoneNumberId) {
        throw new Error('Configuração do Meta API não encontrada ou Phone Number ID não configurado');
      }

      // Validar Phone Number ID
      this.validatePhoneNumberId(this.config.phoneNumberId);

      const phoneE164 = this.formatPhoneToE164(phoneNumber);
      const url = `${this.getBaseUrl()}/${this.config.phoneNumberId}/messages`;

      const body = {
        messaging_product: 'whatsapp',
        to: phoneE164,
        type: 'text',
        text: {
          body: message
        }
      };

      const response = await fetch(url, {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify(body)
      });

      const data = await response.json();

      if (!response.ok) {
        let errorMessage = data.error?.message || data.error?.error_user_msg || `Erro ${response.status}: ${response.statusText}`;
        
        if (errorMessage.includes('does not exist') || errorMessage.includes('cannot be loaded')) {
          errorMessage = `Phone Number ID inválido ou não encontrado.\n\n` +
            `O Phone Number ID deve ser um número (ex: 123456789012345), não um número de telefone formatado.\n\n` +
            `Para encontrar o Phone Number ID correto:\n` +
            `1. Acesse: Meta Business Manager → WhatsApp → API Setup\n` +
            `2. Na seção "Phone numbers", você verá o ID (não o número de telefone)\n` +
            `3. Copie apenas o ID numérico (sem +, sem espaços)\n\n` +
            `Erro original: ${errorMessage}`;
        }
        
        throw new Error(errorMessage);
      }

      return data as MetaApiMessageResponse;
    } catch (error: any) {
      console.error('❌ Erro ao enviar mensagem de texto:', error);
      throw error;
    }
  }

  async sendTemplateMessage(
    phoneNumber: string,
    templateName: string,
    parameters: string[] = [],
    languageCode?: string
  ): Promise<MetaApiMessageResponse> {
    // Usar idioma do template configurado ou padrão
    const templateLang = languageCode || this.config?.templateLanguage || 'pt_BR';
    try {
      if (!this.config || !this.config.phoneNumberId) {
        throw new Error('Configuração do Meta API não encontrada ou Phone Number ID não configurado');
      }

      // Validar Phone Number ID
      this.validatePhoneNumberId(this.config.phoneNumberId);

      const phoneE164 = this.formatPhoneToE164(phoneNumber);
      const url = `${this.getBaseUrl()}/${this.config.phoneNumberId}/messages`;

      const body: any = {
        messaging_product: 'whatsapp',
        to: phoneE164,
        type: 'template',
        template: {
          name: templateName,
          language: {
            code: templateLang
          }
        }
      };

      // Adicionar parâmetros se houver
      // IMPORTANTE: A Meta API usa positional parameters, onde cada variável precisa de um parâmetro
      // na ordem correta, separado por componente (HEADER, BODY)
      // 
      // Estrutura esperada:
      // - Se HEADER tem variáveis: primeiro parâmetro(s) vai(m) no HEADER
      // - Parâmetros restantes vão no BODY
      // - HEADER com mídia (IMAGE, VIDEO, DOCUMENT) não recebe parâmetros de texto
      // - BUTTONS não recebe parâmetros
      //
      // Exemplos:
      // - Template com HEADER {{1}} e BODY {{1}} {{2}}: [headerParam, bodyParam1, bodyParam2]
      // - Template com BODY {{text}} {{amount}} {{date}}: [bodyParam1, bodyParam2, bodyParam3]
      // - Template com HEADER mídia e BODY {{1}} {{2}}: [bodyParam1, bodyParam2]
      
      if (parameters && parameters.length > 0) {
        const components: any[] = [];
        let paramIndex = 0;
        
        // Tentar distribuir parâmetros de forma inteligente
        // Como não temos acesso aos detalhes do template aqui, vamos usar heurística:
        // - Se tem 1 parâmetro: vai no BODY
        // - Se tem 2 parâmetros: primeiro no HEADER, segundo no BODY
        // - Se tem 3+ parâmetros: primeiro no HEADER, resto no BODY
        
        // NOTA: A lógica ideal seria buscar os detalhes do template antes de enviar,
        // mas para manter compatibilidade, usamos esta heurística
        
        if (parameters.length === 1) {
          // Apenas 1 parâmetro: geralmente vai no BODY
          components.push({
            type: 'body',
            parameters: [{
              type: 'text',
              text: parameters[0]
            }]
          });
        } else if (parameters.length === 2) {
          // 2 parâmetros: pode ser HEADER {{1}} + BODY {{1}} ou ambos no BODY
          // Para o template "clientes_inativos": primeiro parâmetro (nome do aluno) vai no HEADER, 
          // segundo parâmetro (nome do usuário logado) vai no BODY
          // Para outros templates: tentar distribuir HEADER + BODY primeiro
          if (templateName === 'clientes_inativos') {
            // Template "clientes_inativos": primeiro no HEADER, segundo no BODY
            components.push({
              type: 'header',
              parameters: [{
                type: 'text',
                text: parameters[0]
              }]
            });
            
            components.push({
              type: 'body',
              parameters: [{
                type: 'text',
                text: parameters[1]
              }]
            });
          } else {
            // Outros templates: tentar ambos no BODY primeiro (mais seguro)
            // Se o template precisar no HEADER, o erro indicará
            components.push({
              type: 'body',
              parameters: parameters.map(param => ({
                type: 'text',
                text: param
              }))
            });
          }
        } else if (parameters.length === 3) {
          // 3 parâmetros: geralmente todos no BODY (ex: {{text}}, {{amount}}, {{date}})
          // Mas pode ser 1 no HEADER + 2 no BODY
          // Vamos tentar todos no BODY primeiro (mais comum para templates de cobrança)
          components.push({
            type: 'body',
            parameters: parameters.map(param => ({
              type: 'text',
              text: param
            }))
          });
        } else {
          // 4+ parâmetros: primeiro no HEADER, resto no BODY
          components.push({
            type: 'header',
            parameters: [{
              type: 'text',
              text: parameters[0]
            }]
          });
          
          // Todos os outros parâmetros vão no BODY
          const bodyParams = parameters.slice(1).map(param => ({
            type: 'text',
            text: param
          }));
          
          components.push({
            type: 'body',
            parameters: bodyParams
          });
        }
        
        body.template.components = components;
      }
      // Se não houver parâmetros, não adicionar components (template sem variáveis)

      const response = await fetch(url, {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify(body)
      });

      const data = await response.json();

      // Log detalhado da resposta para debug
      if (response.ok && data.messages && data.messages[0]) {
        console.log('✅ Resposta da API - Mensagem aceita:', {
          messageId: data.messages[0].id,
          status: data.messages[0].message_status || 'unknown',
          contacts: data.contacts,
          messagingProduct: data.messaging_product,
          waId: data.contacts?.[0]?.wa_id
        });
        
        // Verificar se o número está no WhatsApp
        if (!data.contacts || !data.contacts[0] || !data.contacts[0].wa_id) {
          console.warn('⚠️ ATENÇÃO: WhatsApp ID não retornado. O número pode não estar no WhatsApp ou não aceitou receber mensagens.');
        }
      }

      if (!response.ok) {
        let errorMessage = data.error?.message || data.error?.error_user_msg || `Erro ${response.status}: ${response.statusText}`;
        const errorCode = data.error?.code;
        
        // Log de erro detalhado
        console.error('❌ Erro ao enviar mensagem:', {
          errorCode,
          errorMessage,
          errorData: data.error,
          phoneNumber: phoneNumber,
          templateName: templateName
        });
        
        if (errorCode === 133010 || errorMessage.includes('Account not registered') || errorMessage.includes('not registered')) {
          errorMessage = `❌ Conta não registrada (Erro 133010)\n\n` +
            `O número de telefone (Phone Number ID: ${this.config?.phoneNumberId}) não está registrado ou não está associado ao Business Account ID configurado.\n\n` +
            `🔍 POSSÍVEIS CAUSAS:\n\n` +
            `1. ⚠️ Número está PENDENTE:\n` +
            `   - O número aparece como "Pendente" no Meta Business Manager\n` +
            `   - O processo de registro ainda não foi completado\n` +
            `   - Aguarde a aprovação ou complete o processo de verificação\n\n` +
            `2. ⚠️ Número de outra conta:\n` +
            `   - O Phone Number ID pertence a uma conta Business diferente\n` +
            `   - O Business Account ID não corresponde ao número selecionado\n\n` +
            `3. ⚠️ Número não está no Business Account:\n` +
            `   - O número não foi adicionado ao Business Account configurado\n` +
            `   - Verifique se o número está na lista de números do Business Account\n\n` +
            `✅ SOLUÇÕES:\n\n` +
            `1. Se o número está PENDENTE:\n` +
            `   - Acesse: Meta Business Manager → WhatsApp → Números de telefone\n` +
            `   - Verifique o status do número\n` +
            `   - Complete o processo de verificação/registro se necessário\n` +
            `   - Aguarde a aprovação antes de tentar enviar mensagens\n\n` +
            `2. Verifique a associação:\n` +
            `   - Meta Business Manager → WhatsApp → API Setup\n` +
            `   - Confirme que o Phone Number ID está listado no Business Account ID configurado\n\n` +
            `3. Use o número correto:\n` +
            `   - Clique em "Listar" na página de configurações\n` +
            `   - Selecione um número que esteja associado ao Business Account ID correto\n` +
            `   - Use apenas números com status "Conectado" ou "Ativo"\n\n` +
            `4. Verifique o Business Account ID:\n` +
            `   - Confirme que o Business Account ID está correto\n` +
            `   - O número deve estar listado quando você lista os números desse Business Account\n\n` +
            `📋 Informações atuais:\n` +
            `- Phone Number ID: ${this.config?.phoneNumberId}\n` +
            `- Business Account ID: ${this.config?.businessAccountId || 'Não configurado'}\n\n` +
            `Erro original: ${errorMessage}`;
        } else if (errorCode === 132000 || errorMessage.includes('Number of parameters does not match') || errorMessage.includes('parameters does not match')) {
          errorMessage = `Número de parâmetros não corresponde ao esperado pelo template "${templateName}".\n\n` +
            `O template espera um número específico de parâmetros ({{1}}, {{2}}, etc.), mas estamos enviando ${parameters.length} parâmetro(s).\n\n` +
            `Soluções:\n` +
            `1. Verifique no Meta Business Manager quantos parâmetros o template "${templateName}" espera\n` +
            `2. O template pode não aceitar parâmetros (alguns templates são apenas texto fixo)\n` +
            `3. Se o template não aceita parâmetros, remova os parâmetros ou use um template que aceite\n\n` +
            `Erro original: ${errorMessage}`;
        } else if (errorCode === 132001 || errorMessage.includes('does not exist in the translation') || errorMessage.includes('Template name does not exist')) {
          errorMessage = `Template "${templateName}" não encontrado no idioma "${templateLang}".\n\n` +
            `Possíveis causas:\n` +
            `1. O nome do template está incorreto\n` +
            `2. O template não existe no idioma "${templateLang}"\n` +
            `3. O template não está aprovado\n\n` +
            `Soluções:\n` +
            `1. Verifique o nome exato do template no Meta Business Manager\n` +
            `2. Verifique em qual idioma o template está aprovado (ex.: "clientes_inativos" em pt_BR)\n` +
            `3. Configure o idioma correto em: Integração → Configurações → Meta API → Template Language\n\n` +
            `Erro original: ${errorMessage}`;
        } else if (errorMessage.includes('does not exist') || errorMessage.includes('cannot be loaded')) {
          if (errorMessage.includes('Template') || errorMessage.includes('template')) {
            errorMessage = `Template "${templateName}" não encontrado.\n\n` +
              `Verifique:\n` +
              `1. Se o nome do template está correto (ex: "clientes_inativos" ou "hello_world")\n` +
              `2. Se o template está aprovado no Meta Business Manager\n` +
              `3. Se o idioma do template está correto\n\n` +
              `Erro original: ${errorMessage}`;
          } else {
            errorMessage = `Phone Number ID inválido ou não encontrado.\n\n` +
              `O Phone Number ID deve ser um número (ex: 123456789012345), não um número de telefone formatado.\n\n` +
              `Para encontrar o Phone Number ID correto:\n` +
              `1. Acesse: Meta Business Manager → WhatsApp → API Setup\n` +
              `2. Na seção "Phone numbers", você verá o ID (não o número de telefone)\n` +
              `3. Copie apenas o ID numérico (sem +, sem espaços)\n\n` +
              `Erro original: ${errorMessage}`;
          }
        } else if (errorMessage.includes('template') || errorMessage.includes('Template')) {
          errorMessage = `Template "${templateName}" não encontrado ou não aprovado.\n\n` +
            `Verifique se o template está aprovado no Meta Business Manager e se o nome está correto.`;
        }
        
        throw new Error(errorMessage);
      }

      return data as MetaApiMessageResponse;
    } catch (error: any) {
      console.error('❌ Erro ao enviar template:', error);
      throw error;
    }
  }

  /**
   * Extrai parâmetros da mensagem formatada para usar no template
   * Para o template "clientes_inativos":
   * - Header: {{1}} = Nome do aluno (primeiro nome)
   * - Body: {{1}} = Nome do aluno (primeiro {{1}} do body)
   * - Body: {{1}} = Nome do usuário logado (segundo {{1}} do body)
   * 
   * IMPORTANTE: O parâmetro 'name' pode ser o nome da empresa, então sempre extraímos da mensagem formatada
   * que já contém o nome do cliente correto.
   * 
   * A API do WhatsApp aplica parâmetros na ordem:
   * 1. Primeiro parâmetro → primeiro {{1}} do header
   * 2. Segundo parâmetro → primeiro {{1}} do body
   * 3. Terceiro parâmetro → segundo {{1}} do body (se houver)
   */
  private extractTemplateParameters(message: string, name: string, userName?: string): string[] {
    const parameters: string[] = [];
    
    // IMPORTANTE: Sempre extrair o nome da mensagem formatada, não do parâmetro 'name'
    // A mensagem formatada já contém o nome do cliente correto (ex: "Olá Pedro!")
    
    // Extrair nome do header (geralmente aparece após "Olá" ou "Olá,")
    // Exemplo: "Olá Pedro!" ou "Olá, Paulo Henrique!"
    const nomeHeaderMatch = message.match(/Olá,?\s*([^!]+)!/i);
    let nomeExtraido = '';
    
    if (nomeHeaderMatch && nomeHeaderMatch[1]) {
      nomeExtraido = nomeHeaderMatch[1].trim();
    } else {
      // Tentar extrair qualquer nome da mensagem (formato: Nome com primeira letra maiúscula)
      const qualquerNomeMatch = message.match(/([A-Z][a-z]+(?:\s+[A-Z][a-z]+)*)/);
      if (qualquerNomeMatch) {
        nomeExtraido = qualquerNomeMatch[1].trim();
      }
    }
    
    if (!nomeExtraido) {
      // Se não encontrou nome na mensagem, usar fallback
      nomeExtraido = 'Cliente';
    }
    
    // Para o template "clientes_inativos" (2 parâmetros):
    // - Header: {{1}} = nome do aluno
    // - Body: {{1}} = nome do usuário logado
    const partesNome = nomeExtraido.split(' ');
    const primeiroNome = partesNome[0]; // Primeiro nome para o header
    const nomeCompleto = nomeExtraido; // Nome completo para o body
    
    // Verificar se é template clientes_inativos procurando pelo nome do usuário na mensagem
    // Se encontrar "Aqui é [nome]" e esse nome for diferente do nome do aluno, é o usuário logado
    const usuarioMatch = message.match(/Aqui é\s+([^d]+)\s+do/i);
    const nomeUsuarioNaMensagem = usuarioMatch ? usuarioMatch[1].trim() : null;
    
    // Adicionar parâmetros na ordem correta
    // Para template clientes_inativos (2 parâmetros):
    // - parameters[0] = header {{1}} (nome do aluno)
    // - parameters[1] = body {{1}} (nome do usuário logado)
    if (this.config?.templateName === 'clientes_inativos' && (userName || nomeUsuarioNaMensagem)) {
      parameters.push(nomeCompleto); // Primeiro parâmetro: header (nome do aluno)
      parameters.push(userName || nomeUsuarioNaMensagem || 'Usuário'); // Segundo parâmetro: body (nome do usuário logado)
    } else {
      parameters.push(primeiroNome); // Primeiro parâmetro para o header
      parameters.push(nomeCompleto); // Segundo parâmetro para o body
    }
    
    return parameters;
  }

  async sendMessageToContact(
    phoneNumber: string,
    name: string,
    message: string,
    useTemplate: boolean = false,
    userName?: string
  ): Promise<{ success: boolean; messageId?: string; status?: string; note?: string; error?: string }> {
    try {
      let response: MetaApiMessageResponse;
      
      if (useTemplate && this.config?.templateName) {
        // Usar template configurado
        const templateLang = this.config.templateLanguage || 'pt_BR';
        
        // Tentar primeiro sem parâmetros (para templates fixos como hello_world)
        try {
          response = await this.sendTemplateMessage(phoneNumber, this.config.templateName, [], templateLang);
        } catch (error: any) {
          // Se falhar por falta de parâmetros, extrair parâmetros da mensagem
          if (error.message && (error.message.includes('parameters') || error.message.includes('132000'))) {
            const parameters = this.extractTemplateParameters(message, name, userName);
            response = await this.sendTemplateMessage(phoneNumber, this.config.templateName, parameters, templateLang);
          } else {
            throw error;
          }
        }
      } else {
        // Tentar enviar como mensagem de texto primeiro
        // Se falhar (por exemplo, primeira mensagem precisa de template), tentar com template
        try {
          response = await this.sendTextMessage(phoneNumber, message);
        } catch (error: any) {
          // Se o erro for sobre precisar de template, tentar com template
          if (error.message && (
            error.message.includes('template') || 
            error.message.includes('Template') ||
            error.message.includes('24 hour') ||
            error.message.includes('first message')
          )) {
            if (this.config?.templateName) {
              const templateLang = this.config.templateLanguage || 'pt_BR';
              // Tentar primeiro sem parâmetros (templates fixos)
              try {
                response = await this.sendTemplateMessage(phoneNumber, this.config.templateName, [], templateLang);
              } catch (templateError: any) {
                // Se falhar por falta de parâmetros, extrair parâmetros da mensagem
                if (templateError.message && (templateError.message.includes('parameters') || templateError.message.includes('132000'))) {
                  const parameters = this.extractTemplateParameters(message, name, userName);
                  response = await this.sendTemplateMessage(phoneNumber, this.config.templateName, parameters, templateLang);
                } else {
                  throw templateError;
                }
              }
            } else {
              throw new Error('Mensagem de texto falhou e nenhum template configurado. Configure um template na configuração do Meta API ou aguarde 24h após o último contato.');
            }
          } else {
            throw error;
          }
        }
      }

      const messageId = response.messages?.[0]?.id;
      const waId = response.contacts?.[0]?.wa_id;

      // Verificar possíveis problemas
      let warningMessage = '';
      
      // Se não tem waId, pode ser que o número não está no WhatsApp
      if (!waId) {
        warningMessage += '\n⚠️ ATENÇÃO: WhatsApp ID não retornado. O número pode não estar no WhatsApp ou não aceitou receber mensagens.';
      }

      return {
        success: true,
        messageId: messageId,
        status: 'sent',
        note: `Mensagem enviada com sucesso via Meta API.\n\n` +
          `📱 Message ID: ${messageId || 'N/A'}\n` +
          `👤 WhatsApp ID: ${waId || 'N/A'}${warningMessage}\n\n` +
          `⏱️ Pode levar alguns segundos a 1-2 minutos para chegar no WhatsApp.\n\n` +
          `🔍 SE A MENSAGEM NÃO CHEGOU, OS MOTIVOS MAIS COMUNS SÃO:\n\n` +
          `1. ⚠️ PRIMEIRA MENSAGEM PRECISA DE TEMPLATE:\n` +
          `   - Se é a primeira vez enviando para esse número, você DEVE usar um template aprovado\n` +
          `   - Configure um template em: Integração → Configurações → Meta API\n\n` +
          `2. ⏰ JANELA DE 24 HORAS:\n` +
          `   - Se passou mais de 24h desde o último contato, precisa usar template\n` +
          `   - Ou aguarde o destinatário iniciar uma conversa primeiro\n\n` +
          `3. 📱 NÚMERO NÃO ESTÁ NO WHATSAPP:\n` +
          `   - Verifique se o número realmente tem WhatsApp instalado\n` +
          `   - O número precisa estar registrado no WhatsApp\n\n` +
          `4. 🚫 NÚMERO NÃO ACEITOU RECEBER MENSAGENS:\n` +
          `   - O destinatário precisa ter opt-in (aceitado receber mensagens)\n\n` +
          `📊 Para verificar o status da mensagem:\n` +
          `- Meta Business Manager → WhatsApp → Mensagens\n` +
          `- Ou configure webhook para receber status em tempo real\n\n` +
          `💡 SOLUÇÃO RÁPIDA: Configure um template aprovado e tente novamente usando template.`
      };
    } catch (error: any) {
      console.error('❌ Erro ao enviar mensagem para contato:', error);
      
      let errorMessage = error.message || 'Erro desconhecido';
      
      // Melhorar mensagens de erro
      if (errorMessage.includes('Rate limit') || errorMessage.includes('429')) {
        errorMessage = 'Limite de requisições excedido. Aguarde alguns instantes e tente novamente.';
      } else if (errorMessage.includes('Unauthorized') || errorMessage.includes('401')) {
        errorMessage = 'Token de acesso inválido ou expirado. Verifique as configurações do Meta API.';
      } else if (errorMessage.includes('Invalid') && errorMessage.includes('phone')) {
        errorMessage = 'Número de telefone inválido. Verifique o formato do número.';
      }
      
      return {
        success: false,
        error: errorMessage
      };
    }
  }

  /**
   * Registra um número de telefone usando a API On-Premises
   * NOTA: Esta API está sendo descontinuada, mas ainda pode funcionar para alguns casos
   */
  async registerPhoneNumberOnPremises(
    request: OnPremisesRegistrationRequest
  ): Promise<OnPremisesRegistrationResponse> {
    try {
      throw new Error(
        'A API On-Premises requer um servidor próprio configurado.\n\n' +
        'Para usar a API On-Premises:\n' +
        '1. Você precisa ter um servidor On-Premises configurado\n' +
        '2. O endpoint seria: https://seu-servidor/v1/account\n' +
        '3. Você precisa fazer o POST diretamente no servidor On-Premises\n\n' +
        'Como alternativa, você pode:\n' +
        '- Completar o registro via Meta Business Manager (interface web)\n' +
        '- Ou usar a Cloud API (que já está configurada no sistema)'
      );
    } catch (error: any) {
      throw error;
    }
  }

  /**
   * Verifica o código de registro recebido via SMS/voice
   */
  async verifyPhoneNumberOnPremises(
    request: OnPremisesVerificationRequest
  ): Promise<{ success: boolean; message: string }> {
    try {
      throw new Error(
        'A verificação via API On-Premises requer um servidor próprio configurado.\n\n' +
        'Para verificar o código:\n' +
        '1. Você precisa ter um servidor On-Premises configurado\n' +
        '2. O endpoint seria: https://seu-servidor/v1/account/verify\n' +
        '3. Você precisa fazer o POST diretamente no servidor On-Premises\n\n' +
        'Como alternativa, você pode completar a verificação via Meta Business Manager.'
      );
    } catch (error: any) {
      throw error;
    }
  }
}

export const metaApiService = new MetaApiService();

